import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Paper, Box, Button 
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { supabase } from '../../lib/supabaseClient';
import { format } from 'date-fns';
import './Dashboard.css';

const UserAnalytics = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [searchMobile, setSearchMobile] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [graphData, setGraphData] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    const { data, error } = await supabase
      .from('user')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching users:', error);
      return;
    }

    setUsers(data);
    setFilteredUsers(data);
    processGraphData(data);
  };

  const processGraphData = (userData) => {
    const usersByDate = userData.reduce((acc, user) => {
      const date = format(new Date(user.created_at), 'yyyy-MM-dd');
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const graphData = Object.entries(usersByDate).map(([date, count]) => ({
      date,
      users: count,
    }));

    setGraphData(graphData);
  };

  const handleSearch = () => {
    let filtered = users;

    if (searchName) {
      filtered = filtered.filter(user => 
        user.name?.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    if (searchMobile) {
      filtered = filtered.filter(user => 
        user.phone?.includes(searchMobile)
      );
    }

    if (startDate) {
      filtered = filtered.filter(user => 
        new Date(user.created_at) >= startDate
      );
    }

    if (endDate) {
      filtered = filtered.filter(user => 
        new Date(user.created_at) <= endDate
      );
    }

    setFilteredUsers(filtered);
  };

  return (
    <div className="user-analytics">
      <h2>User Analytics</h2>
      
      {/* Bar Chart (replacing Line Chart) */}
      <div style={{ height: '400px', marginBottom: '2rem' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="users" fill="#8884d8" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Search Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search by Name"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
        />
        <TextField
          label="Search by Mobile"
          value={searchMobile}
          onChange={(e) => setSearchMobile(e.target.value)}
        />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="From Date"
            value={startDate}
            onChange={setStartDate}
          />
          <DatePicker
            label="To Date"
            value={endDate}
            onChange={setEndDate}
          />
        </LocalizationProvider>
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
      </Box>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>S. No.</TableCell>
              <TableCell>Sign up Date</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Mobile Number</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user, index) => (
              <TableRow key={user.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  {format(new Date(user.created_at), 'dd/MM/yyyy')}
                </TableCell>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.phone}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default UserAnalytics;